import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { Seo } from "../components/Seo";
import { GoogleButton } from "../components/GoogleAuth";

const inputCls =
    "w-full rounded-2xl border border-ink/10 bg-white px-5 py-3.5 text-base text-ink placeholder:text-smoke/60 transition-colors focus:border-saffron focus:outline-none";

const LoginPage = () => {
    const { user, login, forgotPassword } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/";

    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [resetSent, setResetSent] = useState(false);

    if (user) return <Navigate to={from} replace />;

    const submitLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        const res = await login(email.trim(), password);
        setBusy(false);
        if (res.ok) navigate(from, { replace: true });
        else setError(res.error);
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        const res = await forgotPassword(email.trim());
        setBusy(false);
        if (res.ok) setResetSent(true);
        else setError(res.error);
    };

    return (
        <>
            <Seo title="Login — SahiKhoj" description="Log in to your SahiKhoj account." path="/login" noindex />
            <section className="flex min-h-[80vh] items-center bg-cream px-6 pb-20 pt-32 sm:px-8">
                <div data-testid="login-page" className="mx-auto w-full max-w-md rounded-3xl border border-ink/5 bg-white p-8 shadow-sm sm:p-10">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink">
                        <span className="h-4 w-4 rounded-full border-[2.5px] border-saffron" />
                    </span>
                    {mode === "login" ? (
                        <>
                            <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink">Welcome back</h1>
                            <p className="mt-2 text-sm text-smoke">Log in to save offers and unlock more of SahiKhoj.</p>
                            <div className="mt-7">
                                <GoogleButton testId="login-google-button" />
                            </div>
                            <div className="mt-5 flex items-center gap-3">
                                <span className="h-px flex-1 bg-ink/10" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-smoke/70">or with email</span>
                                <span className="h-px flex-1 bg-ink/10" />
                            </div>
                            <form onSubmit={submitLogin} className="mt-5 space-y-4" data-testid="login-form">
                                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={inputCls} data-testid="login-email-input" autoComplete="email" />
                                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} data-testid="login-password-input" autoComplete="current-password" />
                                {error && <p data-testid="login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                                <button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full rounded-full bg-saffron px-8 py-4 text-base font-medium text-white transition-colors hover:bg-saffron-hover disabled:opacity-60">
                                    {busy ? "Logging in…" : "Log in"}
                                </button>
                            </form>
                            <div className="mt-5 flex items-center justify-between text-sm">
                                <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link" className="font-medium text-india transition-colors hover:text-saffron">
                                    Forgot password?
                                </button>
                                <Link to="/signup" data-testid="go-signup-link" className="font-medium text-india transition-colors hover:text-saffron">
                                    Create account
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink">Reset password</h1>
                            <p className="mt-2 text-sm text-smoke">Enter your account email and we'll send a reset link.</p>
                            {resetSent ? (
                                <div data-testid="forgot-success" className="mt-8 flex items-start gap-3 rounded-2xl bg-leaf/10 p-5">
                                    <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-leaf" />
                                    <p className="text-sm leading-relaxed text-ink">
                                        If an account exists for {email}, a password reset link is on its way.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={submitForgot} className="mt-8 space-y-4" data-testid="forgot-form">
                                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={inputCls} data-testid="forgot-email-input" autoComplete="email" />
                                    {error && <p data-testid="forgot-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                                    <button type="submit" disabled={busy} data-testid="forgot-submit-button" className="w-full rounded-full bg-saffron px-8 py-4 text-base font-medium text-white transition-colors hover:bg-saffron-hover disabled:opacity-60">
                                        {busy ? "Sending…" : "Send reset link"}
                                    </button>
                                </form>
                            )}
                            <button type="button" onClick={() => { setMode("login"); setError(""); setResetSent(false); }} data-testid="back-to-login" className="mt-5 text-sm font-medium text-india transition-colors hover:text-saffron">
                                Back to login
                            </button>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default LoginPage;
